import Sidebar from "../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { DollarSign as DollarIcon, AlertTriangle as AlertIcon, Mail, CheckCircle, XCircle, Search } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { 
  getAllValoresPendientes, 
  uploadPaymentFile, 
  sendEmailsToAll,
  getAllEstatusValoresPendientes,
  getValoresPendientesByEstatus,
  sendEmailsByEstatus,
  ValorPendiente,
  EstatusValorPendiente,
  GetValoresByEstatusRequest,
  SendEmailsByEstatusRequest
} from "../../../api/paymentService";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
// import { Checkbox } from "../../../components/ui/checkbox"; // No disponible
import { Loader2 } from "lucide-react";
import Swal from "sweetalert2";

export default function PendingPaymentsPage() {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  
  // Estados existentes
  const [valoresPendientes, setValoresPendientes] = useState<ValorPendiente[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedValor, setSelectedValor] = useState<ValorPendiente | null>(null);
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  // Nuevos estados para filtros
  const [estatusOptions, setEstatusOptions] = useState<EstatusValorPendiente[]>([]);
  const [selectedEstatus, setSelectedEstatus] = useState<string[]>([]);
  const [enviadoFilter, setEnviadoFilter] = useState<boolean>(false);
  const [isLoadingEstatus, setIsLoadingEstatus] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isUploadCollapsed, setIsUploadCollapsed] = useState(true);

  // Cargar estatus disponibles
  const fetchEstatusOptions = async () => {
    setIsLoadingEstatus(true);
    try {
      const estatus = await getAllEstatusValoresPendientes();
      setEstatusOptions(estatus);
    } catch (err) {
      console.error("Error al cargar estatus:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los estatus disponibles",
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoadingEstatus(false);
    }
  };

  // Función para cargar los valores pendientes (método original)
  const fetchValoresPendientes = async () => {
    if (!unidadId) {
      setError("No se encontró el código de la unidad residencial.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        vpt_unidad_residencial: unidadId,
        vpt_apartamento: "",
        vpt_encargado: "",
        vpt_estatus: "",
      };
      const data = await getAllValoresPendientes(payload);
      setValoresPendientes(data);
      if (data.length === 0) {
        Swal.fire({
          icon: "info",
          title: "Sin datos",
          text: "No se encontraron valores pendientes para esta unidad.",
          confirmButtonColor: "#1a3c34",
        });
      }
    } catch (err) {
      const errorMessage = "Error al cargar los valores pendientes: " + (err as Error).message;
      setError(errorMessage);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Nueva función para buscar con filtros
  const searchWithFilters = async () => {
    if (!unidadId) {
      setError("No se encontró el código de la unidad residencial.");
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      // Construir payload con los filtros seleccionados
      const payload: GetValoresByEstatusRequest = {
        vpt_unidad_residencial: unidadId,
        vpt_estatus1: selectedEstatus[0] || "",
        vpt_estatus2: selectedEstatus[1] || "",
        vpt_estatus3: selectedEstatus[2] || "",
        vpt_estatus4: selectedEstatus[3] || "",
        vpt_enviado: enviadoFilter ? "1" : "0",
      };

      const data = await getValoresPendientesByEstatus(payload);
      setValoresPendientes(data);
      
      if (data.length === 0) {
        Swal.fire({
          icon: "info",
          title: "Sin resultados",
          text: "No se encontraron valores pendientes con los filtros seleccionados.",
          confirmButtonColor: "#1a3c34",
        });
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      
      // Mostrar error específico si code es -1
      Swal.fire({
        icon: "info",
        title: "Sin resultados",
        text: errorMessage,
        confirmButtonColor: "#1a3c34",
      });
      
      // Limpiar la tabla
      setValoresPendientes([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchValoresPendientes();
    fetchEstatusOptions();
  }, [unidadId]);

  // Manejar selección de estatus
  const handleEstatusChange = (estatusCode: string, checked: boolean) => {
    if (checked) {
      // Agregar estatus (máximo 4)
      if (selectedEstatus.length < 4) {
        setSelectedEstatus([...selectedEstatus, estatusCode]);
      }
    } else {
      // Remover estatus
      setSelectedEstatus(selectedEstatus.filter(code => code !== estatusCode));
    }
  };

  // Manejar selección de todos los estatus
  const handleSelectAllEstatus = (checked: boolean) => {
    if (checked) {
      // Seleccionar todos los estatus disponibles (máximo 4)
      const allCodes = estatusOptions.slice(0, 4).map(estatus => estatus.evp_codigo);
      setSelectedEstatus(allCodes);
    } else {
      // Deseleccionar todos
      setSelectedEstatus([]);
    }
  };

  // Manejar selección de archivo
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type.includes("excel") || file.type.includes("spreadsheet")) {
        setSelectedFile(file);
      } else {
        Swal.fire({
          icon: "error",
          title: "Archivo inválido",
          text: "Por favor, selecciona un archivo Excel (.xlsx o .xls).",
          confirmButtonColor: '#1a3c34',
        });
      }
    }
  };

  // Función para subir el archivo y refrescar la lista al éxito
  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire({
        icon: "warning",
        title: "Falta archivo",
        text: "Selecciona un archivo Excel primero.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Enviar el archivo directamente sin convertir a Base64
      const response = await uploadPaymentFile(selectedFile);
      
      // Verificar el código de respuesta
      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: response.message || "Archivo subido exitosamente.",
          confirmButtonColor: "#1a3c34",
        });

        // Refrescar la lista de valores pendientes
        await fetchValoresPendientes();
        setSelectedFile(null);
        // Limpiar el input de archivo
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        // Si el código no es 1, mostrar error en SweetAlert
        Swal.fire({
          icon: "error",
          title: "Error al procesar el archivo",
          text: response.message || "Error desconocido al procesar el archivo",
          confirmButtonColor: "#1a3c34",
        });
        setError(response.message || "Error al procesar el archivo");
      }
    } catch (err) {
      const errorMessage = (err as Error).message || "Error al subir el archivo";
      setError(errorMessage);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Función principal para enviar correos (unificada)
  const handleSendEmails = async () => {
    const hasFilters = selectedEstatus.length > 0 || enviadoFilter;
    const result = await Swal.fire({
      icon: "question",
      title: "Confirmar Envío",
      text: hasFilters 
        ? "¿Estás seguro de que quieres enviar correos con los filtros seleccionados?"
        : "¿Estás seguro de que quieres enviar correos a todos los propietarios con valores pendientes?",
      showCancelButton: true,
      confirmButtonColor: "#1a3c34",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setIsSendingEmails(true);
      try {
        let response;
        
        if (hasFilters) {
          // Enviar con filtros
          const payload: SendEmailsByEstatusRequest = {
            vpt_unidad_residencial: unidadId,
            vpt_estatus1: selectedEstatus[0] || "",
            vpt_estatus2: selectedEstatus[1] || "",
            vpt_estatus3: selectedEstatus[2] || "",
            vpt_estatus4: selectedEstatus[3] || "",
            vpt_enviado: enviadoFilter ? "1" : "0",
          };
          response = await sendEmailsByEstatus(payload);
        } else {
          // Enviar a todos
          const payload = {
            vpt_unidad_residencial: unidadId,
            vpt_enviado: "string"
          };
          response = await sendEmailsToAll(payload);
        }
        
        // Verificar el código de respuesta
        if (response.code === "1" || response.code === 1) {
          Swal.fire({
            icon: "success",
            title: "Notificaciones Enviadas",
            text: hasFilters 
              ? "Los correos han sido enviados exitosamente según los filtros seleccionados."
              : "Todos los propietarios han sido notificados exitosamente.",
            confirmButtonColor: "#1a3c34",
          });
          
          // Refrescar la lista después de enviar
          if (hasFilters) {
            await searchWithFilters();
          } else {
            await fetchValoresPendientes();
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.message || "Error al enviar los correos.",
            confirmButtonColor: "#1a3c34",
          });
        }
        
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al enviar los correos: " + (err as Error).message,
          confirmButtonColor: "#1a3c34",
        });
      } finally {
        setIsSendingEmails(false);
      }
    }
  };

  const openModal = (valor: ValorPendiente) => {
    setSelectedValor(valor);
  };

  const closeModal = () => {
    setSelectedValor(null);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      {/* Sidebar */}
      <Sidebar />

      {/* Contenido principal */}
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-military-600">Valores Pendientes de Administración</h2>

        {/* Sección de Upload - Colapsable */}
        <Card className="shadow-xl mb-4 sm:mb-6 border-l-4 border-military-500 rounded-lg bg-white">
          <CardHeader className="cursor-pointer" onClick={() => setIsUploadCollapsed(!isUploadCollapsed)}>
            <CardTitle className="text-lg sm:text-xl text-military-600 flex items-center justify-between">
              <span>Subir Documentos Excel</span>
              <button className="text-military-600 hover:text-military-800 transition text-xl sm:text-2xl font-bold">
                {isUploadCollapsed ? "▼" : "▲"}
              </button>
            </CardTitle>
          </CardHeader>
          {!isUploadCollapsed && (
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Instrucciones:</strong> Selecciona un archivo Excel (.xlsx o .xls) con los valores pendientes de administración.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar archivo Excel
                  </label>
                  <Input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {selectedFile && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium">{selectedFile.name}</span>
                      <span className="text-gray-400">
                        ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-full sm:w-auto">
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading || !selectedFile}
                    className="w-full sm:w-auto bg-military-500 hover:bg-military-600 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Subir Archivo
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Sección de Filtros */}
        <Card className="shadow-xl mb-4 sm:mb-6 border-l-4 border-blue-500 rounded-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl text-military-600 flex items-center gap-2">
              Filtros de Búsqueda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Filtros de Estatus */}
              <div>
                {isLoadingEstatus ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-military-500" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Opción Seleccionar Todas */}
                    <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                      <input
                        type="checkbox"
                        id="select-all-estatus"
                        checked={selectedEstatus.length === Math.min(estatusOptions.length, 4)}
                        onChange={(e) => handleSelectAllEstatus(e.target.checked)}
                        className="h-4 w-4 text-military-500 border-gray-300 rounded focus:ring-military-500 focus:ring-2"
                      />
                      <label 
                        htmlFor="select-all-estatus"
                        className="text-sm font-semibold text-military-600 cursor-pointer"
                      >
                        Seleccionar Todas
                      </label>
                    </div>
                    
                    {/* Checkboxes de estatus */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {estatusOptions.map((estatus) => (
                        <div key={estatus.evp_codigo} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`estatus-${estatus.evp_codigo}`}
                            checked={selectedEstatus.includes(estatus.evp_codigo)}
                            onChange={(e) => 
                              handleEstatusChange(estatus.evp_codigo, e.target.checked)
                            }
                            disabled={!selectedEstatus.includes(estatus.evp_codigo) && selectedEstatus.length >= 4}
                            className="h-4 w-4 text-military-500 border-gray-300 rounded focus:ring-military-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <label 
                            htmlFor={`estatus-${estatus.evp_codigo}`}
                            className="text-sm font-medium text-gray-700 cursor-pointer"
                          >
                            {estatus.evp_nombre}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Filtro de Enviado */}
              <div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="enviado-filter"
                    checked={enviadoFilter}
                    onChange={(e) => setEnviadoFilter(e.target.checked)}
                    className="h-4 w-4 text-military-500 border-gray-300 rounded focus:ring-military-500 focus:ring-2"
                  />
                  <label 
                    htmlFor="enviado-filter"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Solo mostrar enviados
                  </label>
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                <Button
                  onClick={searchWithFilters}
                  disabled={isSearching}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                >
                  {isSearching ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Buscar con Filtros
                    </>
                  )}
                </Button>

                {valoresPendientes.length > 0 && (
                  <Button
                    onClick={handleSendEmails}
                    disabled={isSendingEmails || isSearching}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSendingEmails ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Enviar Correos
                      </>
                    )}
                  </Button>
                )}

                <Button
                  onClick={fetchValoresPendientes}
                  disabled={isLoading}
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cargando...
                    </>
                  ) : (
                    "Ver Todos"
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tarjeta de Valores Pendientes */}
        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl text-military-600 flex items-center gap-2">
              Lista de Valores Pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(isLoading || isSearching) ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-8 w-8 animate-spin text-military-500" />
              </div>
            ) : valoresPendientes.length === 0 ? (
              <p className="text-gray-600 text-center py-4">No hay valores pendientes disponibles.</p>
            ) : (
              <>
                {/* Vista de Cards para móvil */}
                <div className="block md:hidden space-y-4">
                  {valoresPendientes.map((valor) => (
                    <div
                      key={valor.vpa_apartamento}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition"
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-500">Apartamento</p>
                            <p className="text-base font-bold text-gray-900">{valor.vpa_apartamento_nombre}</p>
                            <p className="text-sm text-gray-600">{valor.vpa_torre_nombre}</p>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                              valor.vpa_estatus === "M"
                                ? "text-red-700 bg-red-50"
                                : valor.vpa_estatus === "D"
                                ? "text-yellow-700 bg-yellow-50"
                                : "text-green-700 bg-green-50"
                            }`}
                          >
                            {valor.vpa_estatus === "M" && <AlertIcon size={14} />}
                            {valor.vpa_estatus_nombre}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500">Propietario</p>
                            <p className="text-sm font-medium text-gray-900 truncate">{valor.vpa_propietario_nombre}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Valor Total</p>
                            <p className="text-sm font-bold text-military-600">${valor.vpa_valor_total.toLocaleString()}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <span
                            className={`text-sm flex items-center gap-1 ${
                              valor.vpa_enviado ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            {valor.vpa_enviado ? (
                              <>
                                <CheckCircle size={16} />
                                Enviado
                              </>
                            ) : (
                              <>
                                <XCircle size={16} />
                                No enviado
                              </>
                            )}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openModal(valor)}
                            className="border-military-500 text-military-500 hover:bg-military-100 text-xs"
                          >
                            Ver Detalle
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Vista de Tabla para desktop */}
                <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <Table className="min-w-full">
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Torre</TableHead>
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Apartamento</TableHead>
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Propietario</TableHead>
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Estatus</TableHead>
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Valor Total</TableHead>
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Enviado</TableHead>
                            <TableHead className="font-bold text-military-600 whitespace-nowrap">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {valoresPendientes.map((valor) => (
                            <TableRow
                              key={valor.vpa_apartamento}
                              className="hover:bg-gray-50 transition"
                            >
                              <TableCell className="font-medium whitespace-nowrap">{valor.vpa_torre_nombre}</TableCell>
                              <TableCell className="font-medium whitespace-nowrap">{valor.vpa_apartamento_nombre}</TableCell>
                              <TableCell className="whitespace-nowrap">{valor.vpa_propietario_nombre}</TableCell>
                              <TableCell>
                                <span
                                  className={`flex items-center gap-1 whitespace-nowrap ${
                                    valor.vpa_estatus === "M"
                                      ? "text-red-600"
                                      : valor.vpa_estatus === "D"
                                      ? "text-yellow-600"
                                      : "text-green-600"
                                  }`}
                                >
                                  {valor.vpa_estatus === "M" && <AlertIcon size={16} />}
                                  {valor.vpa_estatus_nombre}
                                </span>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">${valor.vpa_valor_total.toLocaleString()}</TableCell>
                              <TableCell>
                                <span
                                  className={`flex items-center gap-1 whitespace-nowrap ${
                                    valor.vpa_enviado ? "text-green-600" : "text-red-600"
                                  }`}
                                >
                                  {valor.vpa_enviado ? (
                                    <>
                                      <CheckCircle size={16} />
                                      Sí
                                    </>
                                  ) : (
                                    <>
                                      <XCircle size={16} />
                                      No
                                    </>
                                  )}
                                </span>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openModal(valor)}
                                  className="border-military-500 text-military-500 hover:bg-military-100"
                                >
                                  Ver Detalle
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Modal de Detalle */}
        {selectedValor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4 text-military-600 flex items-center gap-2">
                Detalle de Valor Pendiente
              </h3>
              <div className="space-y-2 text-sm">
                <p><strong>Torre:</strong> {selectedValor.vpa_torre_nombre}</p>
                <p><strong>Apartamento:</strong> {selectedValor.vpa_apartamento_nombre}</p>
                <p><strong>Propietario:</strong> {selectedValor.vpa_propietario_nombre} ({selectedValor.vpa_propietario})</p>
                <p><strong>Encargado:</strong> {selectedValor.vpa_encargado_nombre} ({selectedValor.vpa_encargado})</p>
                <p><strong>Estatus:</strong> {selectedValor.vpa_estatus_nombre}</p>
                <p><strong>1-30 días:</strong> ${selectedValor.vpa_1_a_30.toLocaleString()}</p>
                <p><strong>31-60 días:</strong> ${selectedValor.vpa_31_a_60.toLocaleString()}</p>
                <p><strong>61-90 días:</strong> ${selectedValor.vpa_61_a_90.toLocaleString()}</p>
                <p><strong>Más de 90 días:</strong> ${selectedValor.vpa_mas_de_90.toLocaleString()}</p>
                <p><strong>Por Vencer:</strong> ${selectedValor.vpa_por_vencer.toLocaleString()}</p>
                <p><strong>Valor Total:</strong> ${selectedValor.vpa_valor_total.toLocaleString()}</p>
                <p className="flex items-center gap-2">
                  <strong>Enviado:</strong> 
                  {selectedValor.vpa_enviado ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle size={16} />
                      Sí
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center gap-1">
                      <XCircle size={16} />
                      No
                    </span>
                  )}
                </p>
              </div>
              <Button
                onClick={closeModal}
                className="mt-6 w-full bg-military-500 hover:bg-military-600 text-white rounded-lg transition"
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}